import React, { useEffect, useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import { Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import { drawPictogram, preloadPictograms } from "./LabelPictograms";

// ---------------------------------------------------------------------------
// Bottle / jar / carton geometry
//
// We size the geometry first, derive the unwrapped label-band aspect ratio,
// then generate a texture canvas at that exact aspect so text doesn't get
// stretched. This is the key change — the previous version used a single 2:1
// canvas for all shapes, which squished text on the jar (very wide unwrap)
// and stretched it on the carton (tall portrait face).
// ---------------------------------------------------------------------------

const LABEL_FONT =
  '"Roboto Condensed", "Archivo Narrow", "Helvetica Neue Condensed", "Helvetica Condensed", "Arial Narrow", Arial, sans-serif';

// EU panels in display order around the body: legal | front | info.
// US panels: info | pdp.
const PANEL_KEYS = {
  EU: ["legal", "front", "information"],
  US: ["info", "pdp"],
};

// Geometry definitions — physical units used by the 3D scene.
const GEOMETRY = {
  cylinder: { kind: "cylinder", radius: 1.3, bodyHeight: 4.2, bandFraction: 0.62, bandOffsetY: -0.2 },
  jar: { kind: "cylinder", radius: 1.85, bodyHeight: 2.3, bandFraction: 0.72, bandOffsetY: 0 },
  box: { kind: "box", width: 2.6, height: 4.0, depth: 1.6, frontPad: 0.06, sidePad: 0.1 },
};

// Compute the unwrapped surface aspect (W / H) so the texture canvas can
// match it and prevent stretching.
const computeBandAspect = (geom) => {
  if (geom.kind === "cylinder") {
    const circ = 2 * Math.PI * geom.radius;
    const h = geom.bodyHeight * geom.bandFraction;
    return circ / h;
  }
  // For box, we render front panel only on the front face (portrait).
  if (geom.kind === "box") {
    return geom.width / geom.height; // ≈ 0.65 (portrait)
  }
  return 2;
};

// ---------------------------------------------------------------------------
// Canvas text helpers
// ---------------------------------------------------------------------------

const wrapText = (ctx, text, maxWidth) => {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const word of words) {
    const trial = current ? `${current} ${word}` : word;
    if (ctx.measureText(trial).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = trial;
    }
  }
  if (current) lines.push(current);
  return lines;
};

const drawTextBlock = (
  ctx,
  x,
  y,
  text,
  { width, fontSize, fontWeight = 400, color = "#111", lineHeight = 1.35, uppercase = false, letterSpacing = 0 }
) => {
  ctx.fillStyle = color;
  ctx.font = `${fontWeight} ${fontSize}px ${LABEL_FONT}`;
  if (typeof ctx.letterSpacing !== "undefined") ctx.letterSpacing = `${letterSpacing}px`;
  const rendered = uppercase ? String(text).toUpperCase() : String(text);
  const lines = wrapText(ctx, rendered, width);
  const lh = fontSize * lineHeight;
  lines.forEach((line, i) => {
    ctx.fillText(line, x, y + (i + 1) * lh - fontSize * 0.18);
  });
  return y + lines.length * lh;
};

// ---------------------------------------------------------------------------
// Per-shape texture generators
// ---------------------------------------------------------------------------

const drawPanel = (
  ctx,
  panel,
  { offsetX, offsetY, width, height, padX = 64, padY = 56, fontScale = 1 }
) => {
  if (!panel) return;
  let cursorY = offsetY + padY;
  const contentW = width - padX * 2;

  // Eyebrow heading
  ctx.fillStyle = "#9CA3AF";
  ctx.font = `600 ${Math.round(22 * fontScale)}px ${LABEL_FONT}`;
  if (typeof ctx.letterSpacing !== "undefined") ctx.letterSpacing = "2.5px";
  ctx.fillText(panel.heading.toUpperCase(), offsetX + padX, cursorY);
  cursorY += Math.round(48 * fontScale);
  if (typeof ctx.letterSpacing !== "undefined") ctx.letterSpacing = "0px";

  // Lines — sized in px scaled by fontScale. When a line carries a `symbol`,
  // we draw the actual regulatory pictogram inline before the text instead
  // of relying on a unicode glyph.
  panel.lines.forEach((line) => {
    const sizeRem = parseFloat(line.fontSize || "0.75rem");
    const fontSizePx = Math.round((sizeRem * 56 + 6) * fontScale);
    const isMissing = line.required && (!line.text || line.text === "—");
    const colour = isMissing ? "#991B1B" : line.color || "#111827";

    if (cursorY + fontSizePx > offsetY + height - padY) return; // skip overflow

    let textOffsetX = offsetX + padX;
    let yWithMargin = cursorY + (line.mt ? Math.round(20 * fontScale) : 0);

    if (line.symbol) {
      // Draw the regulatory pictogram inline. Size scales with the line's
      // font so it visually anchors with the surrounding text.
      const picSize = Math.round(fontSizePx * 1.7);
      const picY = yWithMargin - Math.round(picSize * 0.15);
      const picValue = line.symbol === "pao" ? line.text : null;
      drawPictogram(ctx, line.symbol, textOffsetX, picY, picSize, picValue);
      textOffsetX += picSize + Math.round(12 * fontScale);
    }

    cursorY = drawTextBlock(
      ctx,
      textOffsetX,
      yWithMargin,
      // For PAO we already render the months inside the pictogram, so the
      // remaining text is the descriptor only ("12M" → blank, since the
      // pictogram conveys the same information).
      line.symbol === "pao" ? "" : (line.text || (isMissing ? "[ missing ]" : "")),
      {
        width: contentW - (textOffsetX - (offsetX + padX)),
        fontSize: fontSizePx,
        fontWeight: line.weight || 400,
        color: colour,
        lineHeight: line.lineHeight || 1.35,
        uppercase: line.uppercase,
        letterSpacing: line.uppercase ? 1.5 : 0,
      }
    );
    cursorY += Math.round(10 * fontScale);
  });

  // Footer pictogram strip — Möbius + e-mark only on the EU legal panel so
  // pictograms don't appear on multiple faces of the carton (the info panel
  // and the band texture used to duplicate them).
  if (panel.heading && /^legal/i.test(panel.heading)) {
    const stripY = offsetY + height - padY - Math.round(48 * fontScale);
    const picSize = Math.round(38 * fontScale);
    const picX = offsetX + padX;
    drawPictogram(ctx, "mobius", picX, stripY, picSize);
    drawPictogram(ctx, "e_mark", picX + picSize + Math.round(16 * fontScale), stripY, picSize);
  }
};

// Wrap-around band texture (used for cylinder + jar). Panels arranged
// left-to-right across the canvas; canvas aspect equals the unwrapped band.
const renderBandTexture = (label, aspectRatio, fontScale = 1) => {
  // Choose a canvas width that gives readable text after wrapping.
  // Cap at 4096 to keep GPU memory in check.
  const width = Math.min(Math.round(900 * aspectRatio), 4096);
  const height = Math.round(width / aspectRatio);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFEFC";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "#E5E3DC";
  ctx.lineWidth = 4;
  ctx.strokeRect(6, 6, width - 12, height - 12);

  const panelKeys = PANEL_KEYS[label.jurisdiction] || ["front"];
  const panelCount = panelKeys.length;
  const panelW = width / panelCount;

  ctx.strokeStyle = "#F0EFE9";
  ctx.lineWidth = 2;
  for (let i = 1; i < panelCount; i += 1) {
    ctx.beginPath();
    ctx.moveTo(i * panelW, 30);
    ctx.lineTo(i * panelW, height - 30);
    ctx.stroke();
  }

  panelKeys.forEach((key, idx) => {
    drawPanel(ctx, label.panels[key], {
      offsetX: idx * panelW,
      offsetY: 0,
      width: panelW,
      height,
      padX: Math.max(40, Math.round(panelW * 0.06)),
      padY: 50,
      fontScale,
    });
  });
  return canvas;
};

// Single-panel texture — used for the carton's individual faces.
const renderSinglePanelTexture = (label, panelKey, aspectRatio) => {
  const width = aspectRatio >= 1 ? 1600 : 1100;
  const height = Math.round(width / aspectRatio);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FFFEFC";
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "#E5E3DC";
  ctx.lineWidth = 4;
  ctx.strokeRect(6, 6, width - 12, height - 12);

  drawPanel(ctx, label.panels[panelKey], {
    offsetX: 0,
    offsetY: 0,
    width,
    height,
    padX: 80,
    padY: 80,
    fontScale: 1.0,
  });
  return canvas;
};

// Public — used by labelGenerator3D.test.js too.
export const renderLabelToCanvas = (label) => renderBandTexture(label, 3.14, 1);

// ---------------------------------------------------------------------------
// Texture builders for each shape
// ---------------------------------------------------------------------------

const buildTextures = (label, shape) => {
  const geom = GEOMETRY[shape] || GEOMETRY.cylinder;
  if (geom.kind === "box") {
    // Real cartons split content across faces — pictograms appear on the
    // legal face only, never on every side. We map:
    //   • +Z front  → marketing PDP (brand / name / function / net content)
    //   • -Z back   → information panel (INCI ingredients + warnings)
    //   • +X right  → legal panel (RP, batch, PAO/BBE, country of origin)
    //   • -X left   → no print (clean ivory carton stock)
    // The US label only has 2 panels (pdp + info), so its narrow sides are
    // intentionally left blank rather than duplicating the back panel.
    const frontKey = label.jurisdiction === "EU" ? "front" : "pdp";
    const backKey = label.jurisdiction === "EU" ? "information" : "info";
    const rightKey = label.jurisdiction === "EU" ? "legal" : null;
    const frontAspect = geom.width / geom.height;
    const sideAspect = geom.depth / geom.height;
    return {
      kind: "box",
      front: renderSinglePanelTexture(label, frontKey, frontAspect),
      back: renderSinglePanelTexture(label, backKey, frontAspect),
      right: rightKey ? renderSinglePanelTexture(label, rightKey, sideAspect) : null,
      left: null,
    };
  }
  // Cylinder + jar — single wrap-around texture sized to the band aspect.
  const aspectRatio = computeBandAspect(geom);
  // Bigger fontScale on the very wide jar so text doesn't shrink with the
  // proportionally taller canvas height.
  const fontScale = aspectRatio > 5 ? 0.78 : 1;
  return {
    kind: "band",
    band: renderBandTexture(label, aspectRatio, fontScale),
  };
};

// ---------------------------------------------------------------------------
// 3D scene — Bottle / Jar / Carton
// ---------------------------------------------------------------------------

const Bottle = ({ textures, shape }) => {
  const geom = GEOMETRY[shape] || GEOMETRY.cylinder;

  if (shape === "box") {
    const w = geom.width;
    const h = geom.height;
    const d = geom.depth;
    const frontPad = geom.frontPad;
    const sidePad = geom.sidePad;
    return (
      <group>
        {/* Carton body — ivory */}
        <mesh castShadow receiveShadow>
          <boxGeometry args={[w, h, d]} />
          <meshStandardMaterial color="#F4EFE6" roughness={0.7} metalness={0} />
        </mesh>
        {/* Front (+Z) — marketing PDP */}
        {textures.front && (
          <mesh position={[0, 0, d / 2 + 0.002]}>
            <planeGeometry args={[w * (1 - frontPad * 2), h * (1 - frontPad * 2)]} />
            <meshStandardMaterial
              map={textures.front}
              roughness={0.55}
              metalness={0.05}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}
        {/* Back (-Z) — INCI ingredients + warnings (info panel) */}
        {textures.back && (
          <mesh position={[0, 0, -(d / 2 + 0.002)]} rotation={[0, Math.PI, 0]}>
            <planeGeometry args={[w * (1 - frontPad * 2), h * (1 - frontPad * 2)]} />
            <meshStandardMaterial
              map={textures.back}
              roughness={0.55}
              metalness={0.05}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}
        {/* Right (+X) — legal panel with PAO/BBE pictograms (EU only) */}
        {textures.right && (
          <mesh position={[w / 2 + 0.002, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
            <planeGeometry args={[d * (1 - sidePad * 2), h * (1 - sidePad * 2)]} />
            <meshStandardMaterial
              map={textures.right}
              roughness={0.55}
              metalness={0.05}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}
        {/* Left (-X) intentionally blank — printed cartons leave one narrow
            side clean (or use it for the barcode flap, which is added at print
            time, not on the design proof). */}
      </group>
    );
  }

  if (shape === "jar") {
    const r = geom.radius;
    const h = geom.bodyHeight;
    const bandH = h * geom.bandFraction;
    return (
      <group>
        {/* Jar body — frosted white glass */}
        <mesh castShadow receiveShadow>
          <cylinderGeometry args={[r, r, h, 64, 1, false]} />
          <meshStandardMaterial color="#FFFEFC" roughness={0.4} metalness={0.05} />
        </mesh>
        {/* Label band — open-ended cylinder so the texture covers the side
            cleanly. Texture aspect already matches the band, so no stretch. */}
        <mesh position={[0, geom.bandOffsetY, 0]} rotation={[0, Math.PI, 0]}>
          <cylinderGeometry args={[r * 1.005, r * 1.005, bandH, 96, 1, true]} />
          <meshStandardMaterial map={textures.band} roughness={0.5} metalness={0} side={THREE.DoubleSide} />
        </mesh>
        {/* Lid — slightly larger radius, deeper colour */}
        <mesh castShadow position={[0, h / 2 + 0.18, 0]}>
          <cylinderGeometry args={[r * 1.04, r * 1.04, 0.36, 64]} />
          <meshStandardMaterial color="#1B2A4A" roughness={0.45} metalness={0.45} />
        </mesh>
        {/* Lid top accent */}
        <mesh position={[0, h / 2 + 0.36, 0]}>
          <cylinderGeometry args={[r * 1.0, r * 1.0, 0.02, 64]} />
          <meshStandardMaterial color="#0F1825" roughness={0.6} metalness={0.55} />
        </mesh>
      </group>
    );
  }

  // Slim eau-de-parfum bottle
  const r = geom.radius;
  const h = geom.bodyHeight;
  const bandH = h * geom.bandFraction;
  return (
    <group>
      {/* Body */}
      <mesh castShadow receiveShadow>
        <cylinderGeometry args={[r, r, h, 64, 1, false]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.18} metalness={0.08} transparent opacity={0.92} />
      </mesh>
      {/* Label band */}
      <mesh position={[0, geom.bandOffsetY, 0]} rotation={[0, Math.PI, 0]}>
        <cylinderGeometry args={[r * 1.005, r * 1.005, bandH, 96, 1, true]} />
        <meshStandardMaterial map={textures.band} roughness={0.5} metalness={0} side={THREE.DoubleSide} transparent />
      </mesh>
      {/* Shoulder */}
      <mesh castShadow position={[0, h / 2 + 0.18, 0]}>
        <cylinderGeometry args={[r * 0.55, r, 0.35, 64]} />
        <meshStandardMaterial color="#1B2A4A" roughness={0.4} metalness={0.35} />
      </mesh>
      {/* Neck */}
      <mesh castShadow position={[0, h / 2 + 0.5, 0]}>
        <cylinderGeometry args={[r * 0.36, r * 0.55, 0.3, 32]} />
        <meshStandardMaterial color="#1B2A4A" roughness={0.4} metalness={0.35} />
      </mesh>
      {/* Cap */}
      <mesh castShadow position={[0, h / 2 + 0.95, 0]}>
        <cylinderGeometry args={[r * 0.46, r * 0.46, 0.7, 32]} />
        <meshStandardMaterial color="#0F1825" roughness={0.55} metalness={0.65} />
      </mesh>
    </group>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

const SHAPES = [
  { key: "cylinder", label: "Bottle" },
  { key: "jar", label: "Jar" },
  { key: "box", label: "Carton" },
];

const Label3DPreview = ({ label }) => {
  const theme = useTheme();
  const [shape, setShape] = useState("cylinder");
  const [textures, setTextures] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!label) return undefined;
    let cancelled = false;
    let disposeFn = () => {};
    // Pre-load the icon images first so drawPictogram can drawImage them
    // synchronously when buildTextures runs.
    preloadPictograms().then(() => {
      if (cancelled) return;
      const canvases = buildTextures(label, shape);
      let result;
      if (canvases.kind === "box") {
        const makeTex = (canvas) => {
          if (!canvas) return null;
          const t = new THREE.CanvasTexture(canvas);
          t.colorSpace = THREE.SRGBColorSpace;
          t.anisotropy = 16;
          t.needsUpdate = true;
          return t;
        };
        result = {
          kind: "box",
          front: makeTex(canvases.front),
          back: makeTex(canvases.back),
          right: makeTex(canvases.right),
          left: makeTex(canvases.left),
        };
      } else {
        const band = new THREE.CanvasTexture(canvases.band);
        band.colorSpace = THREE.SRGBColorSpace;
        band.anisotropy = 16;
        band.needsUpdate = true;
        result = { kind: "band", band };
      }
      setTextures(result);
      disposeFn = () => {
        ["front", "back", "right", "left", "band"].forEach((k) => {
          if (result[k]) result[k].dispose();
        });
      };
    });
    return () => {
      cancelled = true;
      disposeFn();
    };
  }, [label, shape]);

  const compliance = useMemo(() => {
    if (!label) return { ready: false, missing: 0 };
    return {
      ready: label.isReady,
      missing: label.missingFields.length,
      annexWarnings: label.annexWarnings?.length || 0,
      stateFindings: label.stateFindings?.length || 0,
    };
  }, [label]);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
        className="label-noprint"
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1}>
          {SHAPES.map((s) => {
            const active = shape === s.key;
            return (
              <Chip
                key={s.key}
                label={s.label}
                size="small"
                onClick={() => setShape(s.key)}
                sx={{
                  cursor: "pointer",
                  // Active = navy brand-mark fill (allow-listed #1B2A4A);
                  // idle defers to the theme glass-inset MuiChip default so
                  // the shape selector reads like a Liquid Glass segmented
                  // control.
                  bgcolor: active ? "#1B2A4A" : undefined,
                  color: active ? "common.white" : undefined,
                  border: active ? "1px solid transparent" : undefined,
                  borderRadius: 999, // explicit pill
                  fontWeight: 600,
                  // Theme spring tint — content-adaptive tint fade.
                  transition: `background ${theme.motion.tint}`,
                }}
              />
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            label={compliance.ready ? "Compliant ✓" : `${compliance.missing} missing`}
            size="small"
            color={compliance.ready ? "success" : "error"}
            sx={{ borderRadius: 999 }}
          />
          {compliance.annexWarnings > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`Annex III · ${compliance.annexWarnings}`}
              sx={{ borderRadius: 999 }}
            />
          )}
          <Tooltip title={expanded ? "Compact view" : "Expand 3D view"} arrow placement="top">
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? "Compact 3D view" : "Expand 3D view"}
              sx={{ ml: 0.5 }}
            >
              {expanded ? (
                <CloseFullscreenRoundedIcon sx={{ fontSize: 14 }} />
              ) : (
                <OpenInFullRoundedIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          width: "100%",
          // Compact by default so the 3D viewer doesn't dominate the page on a
          // standard desktop. Expand toggle bumps it to 540px for a closer look.
          height: expanded ? { xs: 480, md: 580 } : { xs: 280, md: 320 },
          // Glass SURFACE chrome around the WebGL canvas — the Canvas itself
          // composites on top, so backdrop-filter sits only on the bezel; the
          // 3D content stays vivid. Tokens come from theme.glass.surface.
          background: theme.glass.surface.background,
          backdropFilter: theme.glass.surface.backdropFilter,
          WebkitBackdropFilter: theme.glass.surface.WebkitBackdropFilter,
          border: theme.glass.surface.border,
          boxShadow: theme.glass.surface.boxShadow,
          // Chrome-tier 18px radius (concentric outer card per spec).
          borderRadius: "18px",
          p: 1, // 8px — just enough breathing room around the WebGL canvas
          overflow: "hidden",
          position: "relative",
          transition: `height ${theme.motion.morph}`,
        }}
      >
        {textures ? (
          <Canvas
            shadows
            // Carton has labels on 4 faces — start at a 3/4 isometric view so
            // the front + right side are both visible from frame 1. Bottle/jar
            // labels wrap around so a flatter front-on framing is fine.
            camera={
              shape === "box"
                ? { position: [4.2, 1.6, 4.6], fov: 32 }
                : { position: [0, 0.4, 5.5], fov: 32 }
            }
            gl={{ antialias: true, preserveDrawingBuffer: true }}
          >
            {/* Local lighting rig — replaces drei's <Environment preset>,
                which fetches an HDR off a remote CDN at runtime and fails
                noisily ("Could not load studio_small_03_1k.hdr") whenever
                the user is offline / on a flaky network / behind a corporate
                proxy. Three lights + a hemisphere fill approximate a studio
                lookdev cheaply and have zero network dependencies. */}
            <ambientLight intensity={shape === "box" ? 0.7 : 0.45} />
            <hemisphereLight
              skyColor="#FFF6E5"
              groundColor="#1B2A4A"
              intensity={shape === "box" ? 0.55 : 0.4}
            />
            <directionalLight
              position={[5, 8, 5]}
              intensity={1.1}
              castShadow
              shadow-mapSize-width={1024}
              shadow-mapSize-height={1024}
            />
            {/* Fill light from the opposite side so the carton's back/-X
                faces aren't lost in shadow. */}
            <directionalLight position={[-5, 4, -5]} intensity={0.45} />
            {/* Front rim accent — adds the mild specular highlight the HDR
                preset used to provide on the bottle and lid edges. */}
            <directionalLight position={[0, 2, 6]} intensity={0.35} />
            <Bottle textures={textures} shape={shape} />
            <ContactShadows position={[0, -2.4, 0]} opacity={0.42} scale={9} blur={2.4} far={4} />
            <OrbitControls
              enablePan={false}
              enableZoom={true}
              minDistance={3.5}
              maxDistance={9}
              minPolarAngle={Math.PI / 4}
              maxPolarAngle={Math.PI / 1.6}
              autoRotate
              // Faster rotation on the carton so all four faces appear within
              // ~15s instead of ~50s.
              autoRotateSpeed={shape === "box" ? 2.4 : 0.6}
            />
          </Canvas>
        ) : (
          <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
            <Typography variant="caption">
              Generating label texture…
            </Typography>
          </Box>
        )}
      </Box>

      <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
        Drag to rotate · scroll to zoom · auto-rotates by default. Texture is
        generated from the live label data ({label?.jurisdiction}
        {label?.language ? ` · ${label.language.toUpperCase()}` : ""}).
      </Typography>
    </Box>
  );
};

export default Label3DPreview;
